import { z } from 'zod';

/**
 * Scope catalogue for agent tokens. Format `domain:action`.
 * User sessions implicitly hold every non-admin scope; agent tokens hold only what was granted.
 * `domain:*` in a grant covers every action of that domain, except the `admin` domain.
 */
export const SCOPES = {
  'profile:read': { description: 'Read the owner profile and public profiles' },
  'profile:write': { description: 'Edit the owner profile, experience and education' },
  'network:read': { description: 'Read connections and follows' },
  'network:write': { description: 'Send, accept or remove connections and follows' },
  'feed:read': { description: 'Read the feed, posts and comments' },
  'feed:write': { description: 'Create posts, comments and reactions as the owner' },
  'messages:read': { description: 'Read message threads the owner participates in' },
  'messages:write': { description: 'Send messages as the owner' },
  'orgs:read': { description: 'Read organisations and memberships' },
  'orgs:write': { description: 'Edit organisations the owner administers' },
  'jobs:read': { description: 'Search and read job postings' },
  'jobs:write': { description: 'Create and edit job postings for owner orgs' },
  'applications:read': { description: 'Read the owner applications' },
  'applications:write': { description: 'Apply to jobs and withdraw applications' },
  'applications:review': {
    description: 'Review and change status of applications to owner org jobs',
  },
  'matching:read': { description: 'Read job/candidate match scores' },
  'ads:read': { description: 'Read campaigns and ad reports' },
  'ads:write': { description: 'Create and edit campaigns and creatives' },
  'ads:spend': { description: 'Activate campaigns that spend money (approval-gated above limit)' },
  'marketing:read': { description: 'Read UTM links and attribution reports' },
  'marketing:write': { description: 'Create UTM links' },
  'metrics:read': { description: 'Read persona metrics dashboards' },
  'alerts:write': { description: 'Create and delete the owner job alerts (saved searches)' },
  'export:read': { description: 'Export the owner data' },
  'agents:manage': { description: 'Manage agent tokens and approvals (never grantable to agents)' },
  'admin:all': { description: 'Platform administration (staff only; never implied by wildcards)' },
  'webhooks:write': { description: 'Create, edit, delete and redeliver owner webhooks' },
  'groups:write': {
    description: 'Create/update groups the owner runs, join groups, and post/moderate in them',
  },
  'events:write': { description: 'Create/update events the owner hosts, and RSVP to events' },
  'reports:write': { description: 'File a report against content or a person for staff review' },
  'notifications:read': {
    description:
      'Read and acknowledge this token\'s notification inbox (pull, SSE, MCP); on by default for new tokens',
  },
  'verification:write': {
    description:
      'Start/confirm org domain or work-email verification, and set employee visibility',
  },
} as const satisfies Record<string, { description: string }>;

export type Scope = keyof typeof SCOPES;
export const ALL_SCOPES = Object.keys(SCOPES) as Scope[];

/** Scopes a signed-in human user holds. */
export const USER_SCOPES: Scope[] = ALL_SCOPES.filter((s) => s !== 'admin:all');

/** Scopes that may be granted to an agent token (agents may never manage agents or administer). */
export const AGENT_GRANTABLE_SCOPES: Scope[] = ALL_SCOPES.filter(
  (s) => s !== 'admin:all' && s !== 'agents:manage',
);

/** Granted to every new agent token unless the owner removes it at creation (docs/09-notifications.md §9). */
export const DEFAULT_AGENT_SCOPES: Scope[] = ['notifications:read'];

export const ScopeSchema = z.enum(ALL_SCOPES as [Scope, ...Scope[]]);
/** A grant is a scope or a `domain:*` wildcard. */
export const ScopeGrantSchema = z.string().regex(/^[a-z]+:(\*|[a-z]+)$/);

export function isScope(value: string): value is Scope {
  return Object.prototype.hasOwnProperty.call(SCOPES, value);
}

export function parseScopes(values: readonly string[]): Scope[] {
  const out: Scope[] = [];
  for (const v of values) {
    if (!isScope(v)) throw new Error(`Unknown scope: ${v}`);
    if (!out.includes(v)) out.push(v);
  }
  return out;
}

export function scopeSatisfies(granted: readonly string[], required: Scope): boolean {
  const [domain] = required.split(':');
  return granted.some((g) => g === required || (domain !== 'admin' && g === `${domain}:*`));
}
